/** Re-export of the shared policy review decision logic (see convex/model/policyReview). */
export * from "../../convex/model/policyReview";

import type { ChangeType, PolicyImpactLevel, PolicyReviewStatus } from "../../convex/model/enums";
import {
  canGeneratePolicySuggestion,
  hasSuggestedPolicyText,
  policyReviewDraftView,
  type PolicyReviewActionState,
  type PolicyReviewDraftView,
  type PolicySuggestionState,
} from "../../convex/model/policyReview";
import { selectedPolicySection } from "./policyEditor";

export type PolicyReviewStatusLabelContext = "dashboard" | "reviewDetail";
export type PolicyImpactLevelLabelContext = "level" | "impact";
export type PolicyReviewChecklistView = {
  readonly readChangeDone: true;
  readonly editPolicyDone: boolean;
  readonly closeReviewDone: boolean;
};

export type PolicyReviewActionButtonView = {
  readonly active: boolean;
  readonly buttonLabel: string;
};

export type PolicySuggestionActionContext = "toolbar" | "replacement";

export type PolicySuggestionActionLabelInput = {
  readonly suggestionState: PolicySuggestionState;
  readonly hasSuggestedText: boolean;
  readonly context: PolicySuggestionActionContext;
};

export type PolicyReviewSectionOption<SectionId extends string = string> = {
  readonly _id: SectionId;
  readonly body: string;
};

export type PolicyReviewWorkbenchReview<Section extends PolicyReviewSectionOption> = {
  readonly policySections: readonly Section[];
  readonly proposedPolicyText?: string | null;
};

export type PolicyReviewWorkbenchView<Section extends PolicyReviewSectionOption> = {
  readonly policySections: readonly Section[];
  readonly selectedSection: Section | null;
  readonly suggestedText: string;
  readonly hasSuggestedText: boolean;
  readonly draft: PolicyReviewDraftView;
  readonly canSavePolicyUpdate: boolean;
  readonly canGeneratePolicySuggestion: boolean;
};


const POLICY_CHANGE_TYPE_LABELS = {
  amended: "Amendment",
  insertion: "New obligation",
  repeal: "Repeal",
} satisfies Record<ChangeType, string>;

const POLICY_CHANGE_TYPE_TONES = {
  amended: "bg-amber-50 text-amber-700 ring-amber-200",
  insertion: "bg-sky-50 text-sky-700 ring-sky-200",
  repeal: "bg-slate-100 text-slate-600 ring-slate-200",
} satisfies Record<ChangeType, string>;

const POLICY_IMPACT_LEVEL_LABELS = {
  level: {
    high: "high",
    medium: "medium",
    low: "low",
  },
  impact: {
    high: "high impact",
    medium: "medium impact",
    low: "low impact",
  },
} satisfies Record<PolicyImpactLevelLabelContext, Record<PolicyImpactLevel, string>>;

const POLICY_IMPACT_LEVEL_TONES = {
  high: "bg-red-50 text-red-700 ring-red-200",
  medium: "bg-amber-50 text-amber-700 ring-amber-200",
  low: "bg-slate-50 text-slate-600 ring-slate-200",
} satisfies Record<PolicyImpactLevel, string>;

const POLICY_REVIEW_STATUS_LABELS = {
  dashboard: {
    pending: "Pending",
    inReview: "In review",
    updated: "Updated",
    notAffected: "No action",
  },
  reviewDetail: {
    pending: "Pending",
    inReview: "In review",
    updated: "Updated",
    notAffected: "No document change",
  },
} satisfies Record<PolicyReviewStatusLabelContext, Record<PolicyReviewStatus, string>>;

const POLICY_REVIEW_STATUS_TONES = {
  pending: "bg-amber-50 text-amber-700 ring-amber-200",
  inReview: "bg-sky-50 text-sky-700 ring-sky-200",
  updated: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  notAffected: "bg-slate-50 text-slate-600 ring-slate-200",
} satisfies Record<PolicyReviewStatus, string>;

const POLICY_REVIEW_DRAFT_TONE_CLASSES = {
  muted: "text-slate-400",
  saved: "text-emerald-700",
  error: "text-red-600",
} satisfies Record<PolicyReviewDraftView["tone"], string>;

const POLICY_REVIEW_ACTION_STATE_MESSAGE_TONE_CLASSES = {
  idle: "text-slate-400",
  saving: "text-slate-400",
  saved: "text-emerald-700",
  error: "text-red-600",
} satisfies Record<PolicyReviewActionState, string>;

export const policyChangeTypeLabel = (type: ChangeType): string =>
  POLICY_CHANGE_TYPE_LABELS[type];

export const policyChangeTypeToneClass = (type: ChangeType): string =>
  POLICY_CHANGE_TYPE_TONES[type];

export const policyImpactLevelLabel = (
  level: PolicyImpactLevel,
  context: PolicyImpactLevelLabelContext = "level",
): string => POLICY_IMPACT_LEVEL_LABELS[context][level];

export const policyImpactLevelToneClass = (level: PolicyImpactLevel): string =>
  POLICY_IMPACT_LEVEL_TONES[level];

export const policyReviewStatusLabel = (
  status: PolicyReviewStatus,
  context: PolicyReviewStatusLabelContext = "reviewDetail",
): string => POLICY_REVIEW_STATUS_LABELS[context][status];

export const policyReviewStatusToneClass = (status: PolicyReviewStatus): string =>
  POLICY_REVIEW_STATUS_TONES[status];

export const policyReviewDraftToneClass = (
  tone: PolicyReviewDraftView["tone"],
): string => POLICY_REVIEW_DRAFT_TONE_CLASSES[tone];

export const policyReviewActionStateMessageToneClass = (
  state: PolicyReviewActionState,
): string => POLICY_REVIEW_ACTION_STATE_MESSAGE_TONE_CLASSES[state];

export const policySuggestionActionLabel = ({
  suggestionState,
  hasSuggestedText,
  context,
}: PolicySuggestionActionLabelInput): string => {
  if (suggestionState === "generating") return "Generating...";
  if (context === "toolbar") return "Generate suggestion";
  return hasSuggestedText ? "Regenerate" : "Generate";
};

export const policyReviewStartActionVisible = (status: PolicyReviewStatus): boolean =>
  status === "pending";

export const policyReviewWorkbenchView = <Section extends PolicyReviewSectionOption>({
  review,
  activeSectionId,
  draftBody,
  saveState,
  saveError,
  hasCompany,
  hasReview,
  suggestionState,
}: {
  readonly review: PolicyReviewWorkbenchReview<Section> | null | undefined;
  readonly activeSectionId: Section["_id"] | null;
  readonly draftBody: string;
  readonly saveState: PolicyReviewActionState;
  readonly saveError: string | null;
  readonly hasCompany: boolean;
  readonly hasReview: boolean;
  readonly suggestionState: PolicySuggestionState;
}): PolicyReviewWorkbenchView<Section> => {
  const policySections = review?.policySections ?? [];
  const selectedSection = selectedPolicySection({ sections: policySections, activeSectionId });
  const suggestedText = review?.proposedPolicyText ?? "";
  const hasSuggestedTextValue = hasSuggestedPolicyText(suggestedText);
  const draft = policyReviewDraftView({
    savedBody: selectedSection?.body ?? null,
    draftBody,
    saveState,
    saveError,
  });

  return {
    policySections,
    selectedSection,
    suggestedText,
    hasSuggestedText: hasSuggestedTextValue,
    draft,
    canSavePolicyUpdate: draft.canSave,
    canGeneratePolicySuggestion: canGeneratePolicySuggestion({
      hasCompany,
      hasReview,
      hasSelectedSection: selectedSection !== null,
      suggestionState,
    }),
  };
};

export const policyReviewChecklistView = ({
  reviewStatus,
  saveState,
}: {
  readonly reviewStatus: PolicyReviewStatus;
  readonly saveState: PolicyReviewActionState;
}): PolicyReviewChecklistView => ({
  readChangeDone: true,
  editPolicyDone: reviewStatus === "updated" || saveState === "saved",
  closeReviewDone: reviewStatus === "updated" || reviewStatus === "notAffected" || saveState === "saved",
});

export const policyReviewUpdateDecisionView = ({
  reviewStatus,
  draftChanged,
  saveState,
}: {
  readonly reviewStatus: PolicyReviewStatus;
  readonly draftChanged: boolean;
  readonly saveState: PolicyReviewActionState;
}): PolicyReviewActionButtonView => ({
  active: reviewStatus === "updated" || draftChanged,
  buttonLabel: saveState === "saving"
    ? "Saving..."
    : reviewStatus === "updated"
      ? "Document updated"
      : "Save update",
});

export const policyReviewNoChangeDecisionView = ({
  reviewStatus,
  decisionState,
}: {
  readonly reviewStatus: PolicyReviewStatus;
  readonly decisionState: PolicyReviewActionState;
}): PolicyReviewActionButtonView => ({
  active: reviewStatus === "notAffected",
  buttonLabel: decisionState === "saving"
    ? "Closing..."
    : reviewStatus === "notAffected"
      ? "Closed"
      : "Close no change",
});
